/**
 * DyslexiaThemeProvider — the child-facing "Reading comfort" theme root
 * (2026-08-01 pedagogy-audit P0: this used to be mounted-but-dead — no
 * useTheme call sites, no persistence, and a blunt whole-tree
 * `filter: contrast()` for high contrast. It is now the real mechanism).
 *
 * Three per-child settings, controlled from the child Preferences page
 * (web-child + rendered on mobile) and the mobile parent SettingsScreen:
 *   • dyslexia-friendly font — swaps the reading stack to OpenDyslexic
 *     (bundled in ./fonts, injected as @font-face at runtime so every app
 *     gets the correct emitted asset URL under any base path);
 *   • high contrast — toggles the `lwa-high-contrast` ROOT CLASS so each
 *     app's token-level CSS overrides (index.html `:root` variables) and
 *     inline consumers via useTheme() switch palettes. NEVER a CSS filter;
 *   • text size — root font-size % scaling (the proven applyTextSize
 *     approach: rem-based sizes all follow the document root).
 *
 * Persistence reuses the EXISTING stores — no new path:
 *   • font + contrast merge-patch into `learnwithai_child_prefs` (the same
 *     object PreferencesPage/SettingsScreen save; read directly here to
 *     avoid a circular import, exactly like BackgroundMusicProvider);
 *   • text size uses `learnwithai_text_size` (the same key web-child's
 *     textSize.ts / ChildNavBar "Aa" button has always used).
 *
 * All labels/settings copy is DISPLAY-ONLY (audio rule #9) — nothing here
 * is ever wired into speak().
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { tokens, highContrastTokens, type DesignTokens } from './tokens';

/** Same child-prefs store PreferencesPage owns (read directly — no cycle). */
const PREFS_KEY = 'learnwithai_child_prefs';
/** Same text-size store web-child's textSize.ts owns. */
const TEXT_SIZE_KEY = 'learnwithai_text_size';

/** Root class toggled on <html> when the dyslexia-friendly font is ON. */
export const DYSLEXIA_FONT_CLASS = 'lwa-dyslexia-font';
/** Root class toggled on <html> when high contrast is ON (token overrides). */
export const HIGH_CONTRAST_CLASS = 'lwa-high-contrast';

export type ReadingTextSize = 'small' | 'default' | 'large' | 'x-large';

/** Root font-size multipliers — keep in sync with web-child textSize.ts. */
export const TEXT_SIZE_SCALE: Record<ReadingTextSize, number> = {
  small: 0.85,
  default: 1,
  large: 1.25,
  'x-large': 1.5,
};

// Bundled OpenDyslexic (SIL OFL — see THIRD-PARTY-NOTICES.md). new URL(…,
// import.meta.url) makes Vite emit the woff per-app with a correct URL under
// any base ('/', '/learn/', capacitor://) — unlike an index.html url(), which
// breaks on deep SPA routes.
const OPEN_DYSLEXIC_REGULAR = new URL('./fonts/OpenDyslexic-Regular.woff', import.meta.url).href;
const OPEN_DYSLEXIC_BOLD = new URL('./fonts/OpenDyslexic-Bold.woff', import.meta.url).href;

let fontFaceInjected = false;
/** Inject the OpenDyslexic @font-face rules once per document (idempotent). */
function ensureDyslexicFontFace(): void {
  if (fontFaceInjected) return;
  try {
    if (document.querySelector('style[data-lwa-dyslexic-font]')) { fontFaceInjected = true; return; }
    const style = document.createElement('style');
    style.setAttribute('data-lwa-dyslexic-font', '');
    style.textContent =
      `@font-face{font-family:'OpenDyslexic';src:url('${OPEN_DYSLEXIC_REGULAR}') format('woff');font-weight:400;font-style:normal;font-display:swap;}` +
      `@font-face{font-family:'OpenDyslexic';src:url('${OPEN_DYSLEXIC_BOLD}') format('woff');font-weight:700;font-style:normal;font-display:swap;}`;
    document.head.appendChild(style);
    fontFaceInjected = true;
  } catch { /* no DOM (tests/SSR) */ }
}

function readThemePrefs(): { dyslexiaFontEnabled: boolean; highContrastEnabled: boolean } {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Record<string, unknown>;
      return {
        dyslexiaFontEnabled: p.dyslexiaFontEnabled === true,
        highContrastEnabled: p.highContrastEnabled === true,
      };
    }
  } catch { /* ignore */ }
  return { dyslexiaFontEnabled: false, highContrastEnabled: false };
}

// Merge-patch into the shared child prefs — never clobbers other prefs
// (same shape as BackgroundMusicProvider.persistMusicPrefs).
function persistThemePrefs(patch: { dyslexiaFontEnabled?: boolean; highContrastEnabled?: boolean }): void {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    const obj = raw ? JSON.parse(raw) : {};
    localStorage.setItem(PREFS_KEY, JSON.stringify({ ...obj, ...patch }));
  } catch { /* ignore */ }
}

function readTextSize(): ReadingTextSize {
  try {
    const v = localStorage.getItem(TEXT_SIZE_KEY) as ReadingTextSize | null;
    if (v && TEXT_SIZE_SCALE[v] !== undefined) return v;
  } catch { /* ignore */ }
  return 'default';
}

interface ThemeContextValue {
  isDyslexicFont: boolean;
  toggleDyslexicFont: () => void;
  isHighContrast: boolean;
  toggleHighContrast: () => void;
  textSize: ReadingTextSize;
  setTextSize: (size: ReadingTextSize) => void;
  /** The active reading font stack (OpenDyslexic-first when the toggle is ON). */
  readingFontFamily: string;
  /** Spread onto a reading surface's root container: applies the font stack
   *  plus token-level high-contrast colors (inline-styled pages can't see the
   *  CSS variables, so they consume the same tokens through here). */
  readingSurfaceStyle: React.CSSProperties;
  tokens: DesignTokens;
}

// Default value (BackgroundMusicProvider pattern): pages render safely in
// tests/SSR without a provider; the real value comes from the app-root mount.
const defaultValue: ThemeContextValue = {
  isDyslexicFont: false,
  toggleDyslexicFont: () => {},
  isHighContrast: false,
  toggleHighContrast: () => {},
  textSize: 'default',
  setTextSize: () => {},
  readingFontFamily: tokens.fontFamily.default,
  readingSurfaceStyle: {},
  tokens,
};

const ThemeContext = createContext<ThemeContextValue>(defaultValue);

interface ThemeProviderProps {
  children: ReactNode;
}

export function DyslexiaThemeProvider({ children }: ThemeProviderProps) {
  const stored = readThemePrefs();
  const [isDyslexicFont, setIsDyslexicFont] = useState(stored.dyslexiaFontEnabled);
  const [isHighContrast, setIsHighContrast] = useState(stored.highContrastEnabled);
  const [textSize, setTextSizeState] = useState<ReadingTextSize>(readTextSize());

  const readingFontFamily = isDyslexicFont ? tokens.fontFamily.dyslexic : tokens.fontFamily.default;

  useEffect(() => {
    ensureDyslexicFontFace();
    try {
      const root = document.documentElement;
      root.classList.toggle(DYSLEXIA_FONT_CLASS, isDyslexicFont);
      root.style.setProperty('--lwa-reading-font', readingFontFamily);
    } catch { /* no DOM */ }
  }, [isDyslexicFont, readingFontFamily]);

  useEffect(() => {
    try {
      document.documentElement.classList.toggle(HIGH_CONTRAST_CLASS, isHighContrast);
    } catch { /* no DOM */ }
  }, [isHighContrast]);

  // Root % scaling (NOT a wrapper-div font-size): explicit rem sizes on lesson
  // and reader surfaces resolve against the html root, so they all follow.
  useEffect(() => {
    try {
      document.documentElement.style.fontSize = `${TEXT_SIZE_SCALE[textSize] * 100}%`;
    } catch { /* no DOM */ }
  }, [textSize]);

  // Stable callbacks (P1-6): recreated only when their captured state changes,
  // so consumers that depend on identity don't churn. The toggles use the
  // functional-updater form so the callback body never closes over a STALE
  // isDyslexicFont/isHighContrast — the deps array stays empty and they never
  // go stale (persisting the freshly-computed `next`).
  const toggleDyslexicFont = useCallback(() => {
    setIsDyslexicFont((cur) => {
      const next = !cur;
      persistThemePrefs({ dyslexiaFontEnabled: next });
      return next;
    });
  }, []);

  const toggleHighContrast = useCallback(() => {
    setIsHighContrast((cur) => {
      const next = !cur;
      persistThemePrefs({ highContrastEnabled: next });
      return next;
    });
  }, []);

  const setTextSize = useCallback((size: ReadingTextSize) => {
    if (TEXT_SIZE_SCALE[size] === undefined) return;
    setTextSizeState(size);
    try { localStorage.setItem(TEXT_SIZE_KEY, size); } catch { /* ignore */ }
  }, []);

  // Memoize the nested reading-surface style so its identity is stable across a
  // no-op re-render (a fresh object literal would defeat downstream memoization
  // and force every inline-styled reading surface to re-render).
  const readingSurfaceStyle = useMemo<React.CSSProperties>(() => ({
    fontFamily: readingFontFamily,
    ...(isHighContrast
      ? { background: highContrastTokens.bg, color: highContrastTokens.text }
      : {}),
  }), [readingFontFamily, isHighContrast]);

  // Memoize the context value on the ACTUAL state it derives from (P1-6): a
  // fresh object every render re-rendered every useTheme/useSunnyShell consumer
  // app-wide even when nothing changed. Deps are complete — the three state
  // values, the derived font stack, the memoized surface style, and the three
  // stable callbacks (tokens is a module constant, intentionally omitted).
  const value = useMemo<ThemeContextValue>(() => ({
    isDyslexicFont,
    toggleDyslexicFont,
    isHighContrast,
    toggleHighContrast,
    textSize,
    setTextSize,
    readingFontFamily,
    readingSurfaceStyle,
    tokens,
  }), [
    isDyslexicFont,
    toggleDyslexicFont,
    isHighContrast,
    toggleHighContrast,
    textSize,
    setTextSize,
    readingFontFamily,
    readingSurfaceStyle,
  ]);

  // Bare wrapper div (mobile index.html relies on `#root > div` for height):
  // sets the inheritance floor for the reading font. High contrast is token
  // overrides via the root class — deliberately NOT a filter/background here.
  return React.createElement(
    ThemeContext.Provider,
    { value },
    React.createElement('div', { style: { fontFamily: readingFontFamily } }, children)
  );
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
